import { useEffect, useMemo, useState } from 'react'
import {
  AlertTriangleIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  Trash2Icon
} from 'lucide-react'
import {
  isConditionLeaf,
  resolveConditionDescription,
  type Condition,
  type ConditionLeaf
} from '@atbash-labs/ontology-agent-wire'
import type {
  ConditionTrigger,
  TriEvalState,
  TriggerEvalResult
} from '@axilon-apps/domains-ontology-ui-state'

import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Separator } from '../ui/separator'
import { TagPicker } from '../tags/TagPicker'
import { cn } from '../../lib/utils'
import { ConditionTreeEditor } from './ConditionTreeEditor'
import { newRcaTrigger, normalizeTriggerFields } from './condition-defaults'
import { LeafConditionDialog } from './LeafConditionDialog'

const TAG_PAGE = 6

/** Operator-facing status for one ordered trigger in the shared editor. */
export type TriggerDisplayStatus = 'healthy' | 'firing' | 'unknown' | 'error'

export function deriveTriggerDisplayStatuses(
  triggers: Array<{ id?: string }>,
  evalByTriggerId?: Map<string, TriggerEvalResult>
): TriggerDisplayStatus[] {
  const states = triggers.map((t): TriEvalState | undefined => {
    if (!t.id || !evalByTriggerId) return undefined
    return evalByTriggerId.get(t.id)?.state
  })
  return states.map((state) => {
    if (state === 'error') return 'error'
    if (state === 'fired') return 'firing'
    if (state === 'not_fired') return 'healthy'
    return 'unknown'
  })
}

export function indexTriggerEvals(
  triggers: TriggerEvalResult[] | undefined
): Map<string, TriggerEvalResult> | undefined {
  if (!triggers) return undefined
  return new Map(triggers.map((result) => [result.trigger_id, result]))
}

export interface TriggerRulesEditorProps {
  triggers: ConditionTrigger[]
  onChange: (triggers: ConditionTrigger[]) => void
  /** Monitored tag paths shown in the collapsible strip (usually the config's tag_paths). */
  tagPaths: string[]
  /** When set (and not readOnly), the monitored-tags section is editable via TagPicker. */
  onTagPathsChange?: (tagPaths: string[]) => void
  /** Highlight the monitored-tags section as missing required tags. */
  tagPathsInvalid?: boolean
  /** Optional live eval keyed by trigger id — drives firing indicators. */
  evalByTriggerId?: Map<string, TriggerEvalResult>
  readOnly?: boolean
}

type LeafModalState =
  | { open: false }
  | {
      open: true
      mode: 'add' | 'edit'
      parentPath: number[]
      leafIndex?: number
      /** True when the root itself is a leaf being edited. */
      replaceRoot?: boolean
      initial?: ConditionLeaf | null
    }

export function TriggerRulesEditor({
  triggers: rawTriggers,
  onChange,
  tagPaths,
  onTagPathsChange,
  tagPathsInvalid = false,
  evalByTriggerId,
  readOnly = false
}: TriggerRulesEditorProps) {
  const triggers = rawTriggers.map(normalizeTriggerFields)
  const [currentIdx, setCurrentIdx] = useState(0)
  const [listOpen, setListOpen] = useState(false)
  const [tagsOpen, setTagsOpen] = useState(true)
  const [tagPage, setTagPage] = useState(0)
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})
  const [modal, setModal] = useState<LeafModalState>({ open: false })

  const canEditTags = !readOnly && typeof onTagPathsChange === 'function'

  useEffect(() => {
    if (currentIdx >= triggers.length) {
      setCurrentIdx(Math.max(0, triggers.length - 1))
    }
  }, [triggers.length, currentIdx])

  useEffect(() => {
    if (tagPathsInvalid) setTagsOpen(true)
  }, [tagPathsInvalid])

  useEffect(() => {
    const pageCount = Math.max(1, Math.ceil(tagPaths.length / TAG_PAGE))
    setTagPage((p) => Math.min(p, pageCount - 1))
  }, [tagPaths.length])

  const idx = Math.min(currentIdx, Math.max(0, triggers.length - 1))
  const current = triggers[idx]

  const displayStatuses = useMemo(
    () => deriveTriggerDisplayStatuses(triggers, evalByTriggerId),
    [triggers, evalByTriggerId]
  )
  const currentEval = current?.id ? evalByTriggerId?.get(current.id) : undefined

  const updateTriggers = (next: ConditionTrigger[]) => onChange(next.map(normalizeTriggerFields))

  const patchCurrent = (patch: Partial<ConditionTrigger>) => {
    if (!current) return
    const next = triggers.slice()
    next[idx] = { ...next[idx], ...patch }
    updateTriggers(next)
  }

  const setCurrentCondition = (condition: Condition) => {
    patchCurrent({ condition })
  }

  const addTrigger = () => {
    const next = [...triggers, newRcaTrigger()]
    updateTriggers(next)
    setCurrentIdx(next.length - 1)
    setListOpen(false)
  }

  const deleteTrigger = () => {
    if (!current || triggers.length <= 1 || readOnly) return
    const next = triggers.filter((_, i) => i !== idx)
    updateTriggers(next)
    setCurrentIdx(Math.max(0, Math.min(idx, next.length - 1)))
  }

  const pageCount = Math.max(1, Math.ceil(tagPaths.length / TAG_PAGE))
  const page = Math.min(tagPage, pageCount - 1)
  const pagedTags = tagPaths.slice(page * TAG_PAGE, page * TAG_PAGE + TAG_PAGE)
  const showMonitoredTags = canEditTags || tagPaths.length > 0

  return (
    <div className="flex flex-col gap-4">
      <div>
        <p className="m-0 text-10 font-semibold uppercase tracking-[0.16em] text-primary">
          Triggers
        </p>
        <h2 className="mt-1.5 m-0 text-lg font-semibold tracking-tight text-foreground">
          Trigger rules
        </h2>
        <p className="mt-1 m-0 text-sm leading-relaxed text-muted-foreground">
          Each trigger is tracked independently. Every active trigger counts.
        </p>
      </div>

      {showMonitoredTags && (
        <Collapsible open={tagsOpen} onOpenChange={setTagsOpen}>
          <div
            className={cn(
              'rounded-lg border bg-card px-4 py-3',
              tagPathsInvalid ? 'border-st-down' : 'border-border'
            )}
          >
            <CollapsibleTrigger asChild>
              <button type="button" className="flex w-full items-center gap-2 text-left">
                <span className="text-11 font-semibold uppercase tracking-wider text-foreground/80">
                  Monitored tags
                  {canEditTags && <span className="text-st-down"> *</span>}
                </span>
                <span className="text-11 text-muted-foreground">{tagPaths.length}</span>
                <span className="flex-1" />
                <ChevronDownIcon
                  className={cn(
                    'size-4 text-muted-foreground transition-transform',
                    tagsOpen && 'rotate-180'
                  )}
                />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              {canEditTags && onTagPathsChange ? (
                <div className="mt-3">
                  <TagPicker
                    value={tagPaths}
                    onChange={onTagPathsChange}
                    allowCustomValue
                    invalid={tagPathsInvalid}
                    idPrefix="rca-tag-picker"
                    placeholder="Search tags…"
                  />
                  {tagPathsInvalid && (
                    <p className="mt-2 m-0 text-xs text-st-down">This field is required.</p>
                  )}
                </div>
              ) : (
                <>
                  <div className="mt-3 overflow-hidden rounded-md border border-border">
                    {pagedTags.map((tp) => (
                      <div
                        key={tp}
                        className="border-b border-border px-3 py-2 font-mono text-xs leading-snug break-all whitespace-normal text-foreground/80 last:border-b-0"
                      >
                        {tp}
                      </div>
                    ))}
                  </div>
                  {tagPaths.length > TAG_PAGE && (
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon-sm"
                        disabled={page === 0}
                        onClick={() => setTagPage((p) => Math.max(0, p - 1))}
                      >
                        <ChevronLeftIcon className="size-3.5" />
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon-sm"
                        disabled={page >= pageCount - 1}
                        onClick={() => setTagPage((p) => Math.min(pageCount - 1, p + 1))}
                      >
                        <ChevronRightIcon className="size-3.5" />
                      </Button>
                      <span className="text-11 text-muted-foreground">
                        Page {page + 1} of {pageCount}
                      </span>
                    </div>
                  )}
                </>
              )}
            </CollapsibleContent>
          </div>
        </Collapsible>
      )}

      <div className="rounded-xl border border-border bg-card p-4">
        {!current ? (
          <div className="flex flex-col items-start gap-3 py-6">
            <p className="m-0 text-sm text-muted-foreground">No triggers yet.</p>
            {!readOnly && (
              <Button type="button" size="sm" onClick={addTrigger}>
                <PlusIcon className="size-3.5" />
                New trigger
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="mb-3.5 flex flex-wrap items-center gap-2.5">
              {evalByTriggerId && <TriggerEvalBadge status={displayStatuses[idx] ?? 'unknown'} />}
              {readOnly ? (
                <h3 className="m-0 min-w-0 flex-1 text-base font-semibold text-foreground">
                  {current.name?.trim() || `Trigger ${idx + 1}`}
                </h3>
              ) : (
                <Input
                  value={current.name ?? ''}
                  onChange={(e) => patchCurrent({ name: e.target.value })}
                  placeholder="Trigger name"
                  className="h-9 flex-1 border-transparent bg-transparent px-2 text-base font-semibold shadow-none hover:border-border focus-visible:border-primary"
                />
              )}
              <Button
                type="button"
                variant="outline"
                size="icon-sm"
                disabled={idx === 0}
                onClick={() => setCurrentIdx(idx - 1)}
                title="Previous trigger"
              >
                <ChevronLeftIcon className="size-3.5" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon-sm"
                disabled={idx >= triggers.length - 1}
                onClick={() => setCurrentIdx(idx + 1)}
                title="Next trigger"
              >
                <ChevronRightIcon className="size-3.5" />
              </Button>
              <span className="whitespace-nowrap text-11 text-muted-foreground">
                Trigger {idx + 1} of {triggers.length}
              </span>
              {!readOnly && (
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onClick={addTrigger}
                    title="New trigger"
                  >
                    <PlusIcon className="size-3.5" />
                  </Button>
                  <Separator orientation="vertical" className="mx-0.5 h-5" />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onClick={deleteTrigger}
                    disabled={triggers.length <= 1}
                    title="Delete trigger"
                  >
                    <Trash2Icon className="size-3.5" />
                  </Button>
                </>
              )}
            </div>

            {readOnly ? (
              <div className="mb-3.5 flex flex-col gap-3">
                <ReadOnlyField label="Purpose" value={current.purpose} empty="No purpose set" />
                <ReadOnlyField
                  label="Activation delay"
                  value={
                    current.on_delay_seconds === undefined
                      ? undefined
                      : `${current.on_delay_seconds}s`
                  }
                  empty="Immediate"
                />
                <ReadOnlyField
                  label="Clear delay"
                  value={
                    current.off_delay_seconds === undefined
                      ? undefined
                      : `${current.off_delay_seconds}s`
                  }
                  empty="Immediate"
                />
                <ReadOnlyField
                  label="Description"
                  hint="operator-facing"
                  value={current.condition.description}
                  empty="No description set"
                />
              </div>
            ) : (
              <div className="mb-3.5 grid gap-3 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <Label className="text-xs text-muted-foreground">Purpose</Label>
                  <Input
                    value={current.purpose ?? ''}
                    onChange={(e) => patchCurrent({ purpose: e.target.value })}
                    placeholder="What this trigger is for"
                    className="h-8"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label className="text-xs text-muted-foreground">
                    Description <span className="text-muted-foreground/70">· operator-facing</span>
                  </Label>
                  <Input
                    value={current.condition.description ?? ''}
                    onChange={(e) =>
                      setCurrentCondition({
                        ...current.condition,
                        description: e.target.value || undefined
                      })
                    }
                    placeholder="Optional"
                    className="h-8"
                  />
                </div>
              </div>
            )}

            <div className="mb-1 flex items-center gap-2.5 px-0.5">
              <span className="text-11 font-semibold uppercase tracking-wider text-muted-foreground">
                Condition
              </span>
              <span className="h-px flex-1 bg-border" />
              <span className="text-11 text-muted-foreground/70">Fires when true</span>
            </div>

            <ConditionTreeEditor
              condition={current.condition}
              readOnly={readOnly}
              evalNodes={currentEval?.nodes}
              collapsed={collapsed}
              onToggleCollapse={(key) => setCollapsed((c) => ({ ...c, [key]: !c[key] }))}
              onChange={setCurrentCondition}
              onAddLeaf={(parentPath) =>
                setModal({ open: true, mode: 'add', parentPath, initial: null })
              }
              onEditLeaf={(parentPath, leafIndex, leaf) =>
                setModal({
                  open: true,
                  mode: 'edit',
                  parentPath,
                  leafIndex,
                  replaceRoot: isConditionLeaf(current.condition) && parentPath.length === 0,
                  initial: leaf
                })
              }
            />
          </>
        )}
      </div>

      <Collapsible open={listOpen} onOpenChange={setListOpen}>
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <CollapsibleTrigger asChild>
            <button type="button" className="flex w-full items-center gap-2 px-3.5 py-3 text-left">
              <ChevronDownIcon
                className={cn(
                  'size-4 text-muted-foreground transition-transform',
                  listOpen && 'rotate-180'
                )}
              />
              <span className="text-11 font-semibold uppercase tracking-wider text-foreground/80">
                All triggers
              </span>
              <span className="text-11 text-muted-foreground">{triggers.length}</span>
              <span className="flex-1" />
              <span className="hidden text-11 text-muted-foreground/70 sm:inline">
                Independently stateful
              </span>
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="border-t border-border">
              {triggers.map((t, i) => {
                const preview =
                  t.condition.description?.trim() ||
                  t.purpose?.trim() ||
                  resolveConditionDescription(t.condition) ||
                  'Empty condition'
                return (
                  <button
                    key={t.id ?? i}
                    type="button"
                    onClick={() => setCurrentIdx(i)}
                    className={cn(
                      'flex w-full items-center gap-3 border-b border-border px-3.5 py-2.5 text-left last:border-b-0',
                      'transition-colors hover:bg-accent/30',
                      i === idx && 'bg-primary/10'
                    )}
                  >
                    <span
                      className={cn(
                        'flex size-5 shrink-0 items-center justify-center rounded-md font-mono text-11 font-semibold',
                        i === idx
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground'
                      )}
                    >
                      {i + 1}
                    </span>
                    {evalByTriggerId && (
                      <TriggerEvalBadge status={displayStatuses[i] ?? 'unknown'} compact />
                    )}
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium">
                        {t.name?.trim() || `Trigger ${i + 1}`}
                      </span>
                      <span className="mt-0.5 block whitespace-normal break-words text-11 leading-snug text-muted-foreground">
                        {preview}
                      </span>
                    </span>
                    {i === idx && (
                      <Badge variant="outline" className="text-10 uppercase tracking-wider">
                        Viewing
                      </Badge>
                    )}
                  </button>
                )
              })}
            </div>
            {!readOnly && (
              <div className="px-3.5 py-3">
                <Button type="button" variant="secondary" size="sm" onClick={addTrigger}>
                  <PlusIcon className="size-3.5" />
                  New trigger
                </Button>
              </div>
            )}
          </CollapsibleContent>
        </div>
      </Collapsible>

      {!readOnly && (
        <LeafConditionDialog
          open={modal.open}
          mode={modal.open ? modal.mode : 'add'}
          initial={modal.open ? modal.initial : null}
          availableTagPaths={tagPaths}
          onOpenChange={(open) => {
            if (!open) setModal({ open: false })
          }}
          onSave={(leaf) => {
            if (!modal.open || !current) return
            setCurrentCondition(applyLeafSave(current.condition, modal, leaf))
          }}
        />
      )}
    </div>
  )
}

function ReadOnlyField({
  label,
  hint,
  value,
  empty
}: {
  label: string
  hint?: string
  value?: string | null
  empty: string
}) {
  const text = value?.trim()
  return (
    <div className="flex flex-col gap-1.5 rounded-lg border border-border bg-muted/15 px-3 py-2.5">
      <span className="text-11 font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
        {hint ? (
          <span className="font-normal normal-case tracking-normal text-muted-foreground/70">
            {' '}
            · {hint}
          </span>
        ) : null}
      </span>
      <p
        className={cn(
          'm-0 whitespace-pre-wrap break-words text-sm leading-relaxed',
          text ? 'text-foreground' : 'text-muted-foreground'
        )}
      >
        {text || empty}
      </p>
    </div>
  )
}

const TRIGGER_EVAL_META: Record<
  TriggerDisplayStatus,
  {
    label: string
    title: string
    textClass: string
    bgClass: string
    dotClass: string
    pulse?: boolean
    icon?: 'alert'
  }
> = {
  healthy: {
    label: 'Healthy',
    title: 'Condition not met',
    textClass: 'text-st-run',
    bgClass: 'bg-st-run-bg',
    dotClass: 'bg-st-run'
  },
  firing: {
    label: 'Active',
    title: 'Condition true',
    textClass: 'text-st-down',
    bgClass: 'bg-st-down-bg',
    dotClass: 'bg-st-down',
    pulse: true
  },
  unknown: {
    label: 'Unknown',
    title: 'Evaluation unavailable',
    textClass: 'text-muted-foreground',
    bgClass: 'bg-muted',
    dotClass: 'bg-muted-foreground/40'
  },
  error: {
    label: 'Error',
    title: 'Evaluation error',
    textClass: 'text-st-down',
    bgClass: 'bg-st-down-bg',
    dotClass: 'bg-st-down',
    icon: 'alert'
  }
}

function TriggerEvalBadge({
  status,
  compact
}: {
  status: TriggerDisplayStatus
  compact?: boolean
}) {
  const m = TRIGGER_EVAL_META[status]
  if (compact) {
    return (
      <span
        title={`${m.label} — ${m.title}`}
        className="inline-flex shrink-0 items-center"
        aria-label={m.label}
      >
        {m.icon === 'alert' ? (
          <AlertTriangleIcon className={cn('size-3', m.textClass)} aria-hidden />
        ) : (
          <span
            className={cn('size-2.5 shrink-0 rounded-full', m.dotClass, m.pulse && 'animate-pulse')}
            aria-hidden
          />
        )}
      </span>
    )
  }
  return (
    <span
      title={m.title}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-10 font-semibold uppercase tracking-wider',
        m.textClass,
        m.bgClass
      )}
    >
      {m.icon === 'alert' ? (
        <AlertTriangleIcon className="size-3" aria-hidden />
      ) : (
        <span
          className={cn('size-2 rounded-full', m.dotClass, m.pulse && 'animate-pulse')}
          aria-hidden
        />
      )}
      {m.label}
    </span>
  )
}

function applyLeafSave(
  root: Condition,
  modal: Extract<LeafModalState, { open: true }>,
  leaf: ConditionLeaf
): Condition {
  if (
    modal.mode === 'edit' &&
    (modal.replaceRoot || (modal.parentPath.length === 0 && isConditionLeaf(root)))
  ) {
    return { ...leaf, description: leaf.description ?? root.description }
  }
  if (modal.mode === 'add') {
    return insertAtPath(root, modal.parentPath, leaf)
  }
  return replaceAtPath(root, [...modal.parentPath, modal.leafIndex ?? 0], leaf)
}

function insertAtPath(root: Condition, parentPath: number[], leaf: ConditionLeaf): Condition {
  if (parentPath.length === 0) {
    if (root.kind === 'and' || root.kind === 'or') {
      return { ...root, operands: [...root.operands, leaf] }
    }
    return { kind: 'and', operands: [root, leaf] }
  }
  return mapAtPath(root, parentPath, (node) => {
    if (node.kind === 'and' || node.kind === 'or') {
      return { ...node, operands: [...node.operands, leaf] }
    }
    return node
  })
}

function replaceAtPath(root: Condition, path: number[], leaf: ConditionLeaf): Condition {
  if (path.length === 0) return leaf
  return mapAtPath(root, path.slice(0, -1), (node) => {
    const i = path[path.length - 1]
    if (node.kind === 'and' || node.kind === 'or') {
      const operands = node.operands.slice()
      operands[i] = leaf
      return { ...node, operands }
    }
    if (node.kind === 'not' && i === 0) {
      return { ...node, operand: leaf }
    }
    return node
  })
}

function mapAtPath(root: Condition, path: number[], fn: (node: Condition) => Condition): Condition {
  if (path.length === 0) return fn(root)
  const [head, ...rest] = path
  if (root.kind === 'and' || root.kind === 'or') {
    const operands = root.operands.slice()
    operands[head] = mapAtPath(operands[head], rest, fn)
    return { ...root, operands }
  }
  if (root.kind === 'not' && head === 0) {
    return { ...root, operand: mapAtPath(root.operand, rest, fn) }
  }
  return root
}
